import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getWeeklyLeaders, getMeta } from "../lib/api";

const CATS = [
  { key: "passing", label: "Passing Yards" },
  { key: "rushing", label: "Rushing Yards" },
  { key: "receiving", label: "Receiving Yards" },
];

const LeaderList = ({ title, players }) => (
  <div data-testid={`weekly-${title.split(" ")[0].toLowerCase()}`} className="border border-border bg-card">
    <div className="px-4 py-2 bg-secondary border-b border-border label-caps">{title}</div>
    {players.length === 0 && <p className="px-4 py-6 text-sm text-muted-foreground">No data for this week.</p>}
    {players.map((p, i) => (
      <Link
        key={p.id + i}
        to={`/player/${p.playerId}`}
        data-testid={`weekly-player-${p.id}`}
        className="flex items-center gap-3 px-4 py-2.5 border-b border-border/40 last:border-0 hover:bg-white/5 transition-colors duration-150"
      >
        <span className="font-display text-lg w-6 text-muted-foreground">{i + 1}</span>
        {p.teamLogo && <img src={p.teamLogo} alt={p.team} className="w-6 h-6 object-contain" />}
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{p.name}</p>
          <p className="text-[11px] text-muted-foreground">{p.sub}</p>
        </div>
        <span className="ml-auto font-display text-2xl text-primary tabular-nums">{p.yards}</span>
      </Link>
    ))}
  </div>
);

export default function WeeklyLeaders() {
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: getMeta });
  const [week, setWeek] = useState(null);
  const activeWeek = week || meta?.statsWeek || meta?.currentWeek || 1;

  const { data, isFetching } = useQuery({
    queryKey: ["weekly", activeWeek],
    queryFn: () => getWeeklyLeaders(activeWeek),
    enabled: !!activeWeek,
  });

  const weeks = Array.from({ length: meta?.statsTotalWeeks || meta?.totalWeeks || 18 }, (_, i) => i + 1);

  return (
    <div data-testid="weekly-page" className="space-y-6">
      <div className="flex items-end justify-between border-b-2 border-border pb-2">
        <h1 className="font-display text-5xl">WEEKLY LEADERS</h1>
        <span data-testid="weekly-season-label" className="label-caps">{meta?.statsSeason} · Top performers by week</span>
      </div>

      <div data-testid="weekly-week-selector" className="flex flex-wrap gap-1 border border-border p-1 bg-card">
        {weeks.map((w) => (
          <button
            key={w}
            data-testid={`weekly-week-${w}`}
            onClick={() => setWeek(w)}
            className={`w-11 h-10 text-sm font-bold transition-colors duration-150 ${
              w === activeWeek ? "bg-primary text-black" : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
            }`}
          >
            {w}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <h2 className="font-display text-3xl text-primary">WEEK {activeWeek}</h2>
        {isFetching && <span className="text-sm text-muted-foreground">loading stats…</span>}
      </div>

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {CATS.map((c) => (
            <LeaderList key={c.key} title={c.label} players={data[c.key] || []} />
          ))}
        </div>
      )}
    </div>
  );
}
